import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import { PageHeader } from '../components/PageHeader';
import { useToast } from '../toast';

interface ResetRequest {
  id: string;
  user: { id: string; email: string; displayName: string };
  requestedAt: string;
  verifiedAt: string | null;
  tenants: string[];
}

export function MfaResets() {
  // Null until loaded, so "No open requests" is never shown to somebody whose list has not arrived yet.
  const [requests, setRequests] = useState<ResetRequest[] | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      const r = await api<{ requests: ResetRequest[] }>('/api/mfa-resets');
      setRequests(r.requests);
    } catch (err) {
      setRequests((current) => current ?? []);
      setError(err instanceof ApiError ? err.message : 'Could not load requests');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function decide(id: string, action: 'approve' | 'decline') {
    setError(null);
    try {
      await api(`/api/mfa-resets/${id}/${action}`, { method: 'POST', body: action === 'approve' ? { note: notes[id] || undefined } : {} });
      toast.ok(action === 'approve' ? 'Approved. They have been emailed a link that works for 15 minutes.' : 'Declined.');
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That did not work');
    }
  }

  return (
    <>
      <PageHeader title="Sign-in reset requests" />
      <p className="lede">
        People in workspaces you own who have lost their authenticator and passkeys. Verify each one yourself,
        for example on a video call with ID shown, before approving. Nothing you see is stored except your note.
      </p>
      {error && <div className="alert">{error}</div>}
      {requests === null && <p className="muted">Loading…</p>}
      {requests?.length === 0 && (
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>No open requests.</p>
        </div>
      )}
      {requests?.map((r) => (
        <div className="card" key={r.id}>
          <h3>
            {r.user.displayName || r.user.email}{' '}
            {r.user.displayName && <span className="muted small" style={{ fontWeight: 400 }}>{r.user.email}</span>}
          </h3>
          <p className="small muted">
            Requested {new Date(r.requestedAt).toLocaleString()} · member of {r.tenants.join(', ')}
            {r.verifiedAt ? ' · already approved, link sent' : ''}
          </p>
          {!r.verifiedAt && (
            <div className="stack">
              <label>
                How you verified them (kept in the audit log)
                <input id={`note-${r.id}`} placeholder="e.g. Video call, passport shown" value={notes[r.id] ?? ''} onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })} />
              </label>
              <div className="actions">
                <button onClick={() => decide(r.id, 'approve')}>Approve reset</button>
                <button className="danger" onClick={() => decide(r.id, 'decline')}>Decline</button>
              </div>
            </div>
          )}
        </div>
      ))}
    </>
  );
}
